import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { toast } from 'sonner';
import {
  Briefcase, Plus, Trash2, Check, User, Package,
  Plane, Search, RotateCcw, AlertTriangle, ChevronDown, Printer, Download,
  Users, Pill, X, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import PageLayout from '../components/PageLayout';

const STATUS_CONFIG = {
  packing:    { label: 'Packing',    color: 'bg-blue-100 text-blue-700 border-blue-200' },
  packed:     { label: 'Packed',     color: 'bg-amber-100 text-amber-700 border-amber-200' },
  in_transit: { label: 'In Transit', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  arrived:    { label: 'Arrived',    color: 'bg-green-100 text-green-700 border-green-200' },
  unpacked:   { label: 'Unpacked',   color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const PREVIOUS_STATUS = { arrived: 'in_transit', in_transit: 'packed', packed: 'packing' };

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.packing;
  return <Badge className={`text-xs px-2 py-0.5 border ${cfg.color} font-medium`}>{cfg.label}</Badge>;
};

// ── Multi-select checkbox popover (matches ParticipantsManagement pattern) ──
const MultiSelectFilter = ({ label, icon: Icon, options, selected, onChange, testId }) => {
  const toggle = (val) =>
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  const summary =
    selected.length === 0 ? `All ${label.toLowerCase()}` :
    selected.length === 1 ? options.find(o => o.value === selected[0])?.label || selected[0] :
    `${selected.length} ${label.toLowerCase()}`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 rounded-full text-sm gap-1.5" data-testid={testId}>
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {label}: <span className="font-medium">{summary}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {options.length === 0 && <div className="text-xs text-slate-400 px-2 py-1">No options</div>}
          {options.map(opt => {
            const isChecked = selected.includes(opt.value);
            return (
              <div
                key={opt.value}
                role="button"
                tabIndex={0}
                onClick={() => toggle(opt.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(opt.value); } }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 text-left text-sm cursor-pointer select-none"
                data-testid={`${testId}-opt-${opt.value}`}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isChecked ? 'bg-primary border-primary text-white' : 'border-slate-300'}`}>
                  {isChecked && <Check className="w-3 h-3" />}
                </span>
                <span className="flex-1 truncate">{opt.label}</span>
              </div>
            );
          })}
        </div>
        {selected.length > 0 && (
          <button
            onClick={() => onChange([])}
            className="mt-2 w-full text-xs text-slate-500 hover:text-slate-700 border-t pt-2"
          >
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};

const TransportBags = () => {
  const [searchParams] = useSearchParams();
  const tripIdParam = searchParams.get('trip_id');

  const [bags, setBags] = useState([]);
  const [trips, setTrips] = useState([]);
  const [participants, setParticipants] = useState([]); // enriched across all trips
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedTripIds, setSelectedTripIds] = useState([]);   // [] = all
  const [selectedStates, setSelectedStates] = useState([]);     // [] = all
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState('bags'); // 'bags' | 'participants'

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPackDialog, setShowPackDialog] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [activeBag, setActiveBag] = useState(null);
  const [packSearch, setPackSearch] = useState('');
  const [packTypeFilter, setPackTypeFilter] = useState('all');
  const [bagForm, setBagForm] = useState({ bag_number: '', trip_id: '' });
  const [packItems, setPackItems] = useState([]);
  // New Inventory Item creation form (inside Edit Contents dialog)
  const [newItemForm, setNewItemForm] = useState(null); // null when closed; {name, type_id, quantity, unit_of_measurement, unit_cost, controlled_substance} when open
  const [inventoryTypes, setInventoryTypes] = useState([]);

  // Controlled-substance warning
  const [pendingAssignment, setPendingAssignment] = useState(null); // {bagId, userId, tripId, bagNumber, participantName}

  // Contents expansion (per-bag)
  const [expandedBags, setExpandedBags] = useState(new Set());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchTrips();
    fetchInventoryItems();
    fetchInventoryTypes();
  }, []);

  // Pre-select trip from URL param once trips load
  useEffect(() => {
    if (tripIdParam && trips.some(t => t.trip_id === tripIdParam)) {
      setSelectedTripIds([tripIdParam]);
    }
  }, [tripIdParam, trips]);

  // Reload data whenever the trip set changes
  useEffect(() => {
    if (trips.length > 0) {
      fetchAllBags();
      fetchAllParticipants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trips]);

  const fetchTrips = async () => {
    try {
      const res = await api.get('/trips');
      const sorted = (res.data || []).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      setTrips(sorted);
    } catch { toast.error('Failed to load trips'); }
  };

  const fetchAllBags = async () => {
    setLoading(true);
    try {
      // Fetch all bags across all trips (no filter → returns everything)
      const res = await api.get('/inventory/bags');
      setBags(res.data || []);
    } catch { toast.error('Failed to load bags'); }
    finally { setLoading(false); }
  };

  const fetchAllParticipants = async () => {
    try {
      // Fetch per trip and merge, tagging each with its trip
      const results = await Promise.all(
        trips.map(async (t) => {
          try {
            const res = await api.get(`/trips/${t.trip_id}/participants`);
            return (res.data || []).map(p => ({ ...p, _trip_id: t.trip_id, _trip_name: t.name }));
          } catch { return []; }
        })
      );
      setParticipants(results.flat());
    } catch { toast.error('Failed to load participants'); }
  };

  const fetchInventoryItems = async () => {
    try {
      const res = await api.get('/inventory/items');
      setInventoryItems(res.data);
    } catch {}
  };

  const fetchInventoryTypes = async () => {
    try {
      const res = await api.get('/inventory/types');
      setInventoryTypes(res.data || []);
    } catch {}
  };

  const itemNameById = (itemId) =>
    inventoryItems.find(i => i.item_id === itemId)?.name || '(removed item)';

  const itemUnitById = (itemId) =>
    inventoryItems.find(i => i.item_id === itemId)?.unit_of_measurement || 'each';

  const tripName = (tripId) => trips.find(t => t.trip_id === tripId)?.name || tripId;

  // Prefer the participant's profile name (kept fresh when they edit their profile)
  // over `user.name` (frozen from the original invite/signup). Falls back to email.
  const participantDisplayName = (p) => {
    const first = p?.profile?.first_name?.trim() || '';
    const last  = p?.profile?.last_name?.trim() || '';
    const combined = `${first} ${last}`.trim();
    return combined || p?.user?.name || p?.user?.email || '(unnamed)';
  };
  // Same rule for the bag's assigned-to chip. We look up the participant by user_id
  // across all trips so an updated profile name wins over the stale user.name the
  // backend embeds on the bag object.
  const displayNameForUserId = (userId, fallbackName) => {
    if (!userId) return fallbackName || '';
    const p = participants.find(pt => (pt.user?.user_id || pt.user_id) === userId);
    if (p) return participantDisplayName(p);
    return fallbackName || '';
  };

  // ── Filter application ─────────────────────────────────────
  const tripFilterSet = useMemo(() =>
    selectedTripIds.length === 0 ? null : new Set(selectedTripIds),
    [selectedTripIds]);

  const stateFilterSet = useMemo(() =>
    selectedStates.length === 0 ? null : new Set(selectedStates.map(s => s.toUpperCase())),
    [selectedStates]);

  const participantMatchesFilters = (p) => {
    if (tripFilterSet && !tripFilterSet.has(p._trip_id)) return false;
    const rawState = (p.profile?.state || '').toUpperCase();
    if (stateFilterSet && !stateFilterSet.has(rawState)) return false;
    return true;
  };

  const bagMatchesFilters = (bag) => {
    if (tripFilterSet && !tripFilterSet.has(bag.trip_id)) return false;
    return true;
  };

  // ── Participant helpers ────────────────────────────────────
  // Year of a trip's start_date (used for "same season" grouping)
  const tripYear = (tripId) => {
    const t = trips.find(t => t.trip_id === tripId);
    if (!t?.start_date) return null;
    const parsed = new Date(t.start_date);
    return isNaN(parsed.getFullYear()) ? null : parsed.getFullYear();
  };
  const sameSeason = (tripIdA, tripIdB) => {
    if (!tripIdA || !tripIdB) return false;
    if (tripIdA === tripIdB) return true;
    const yA = tripYear(tripIdA); const yB = tripYear(tripIdB);
    return yA !== null && yA === yB;
  };
  // Bags this user carries in the same season (year) as tripId — back-to-back weeks share
  const bagsForUserOnTrip = (userId, tripId) =>
    bags.filter(b => b.assigned_to_id === userId && sameSeason(b.trip_id, tripId));

  const filteredParticipants = useMemo(() => {
    let list = participants.filter(participantMatchesFilters);
    if (unassignedOnly) {
      list = list.filter(p => {
        const uid = p.user?.user_id || p.user_id;
        if (p.no_bag_needed) return false;
        return bagsForUserOnTrip(uid, p._trip_id).length === 0;
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participants, bags, tripFilterSet, stateFilterSet, unassignedOnly, trips]);

  const filteredBags = useMemo(() => {
    let list = bags.filter(bagMatchesFilters);
    if (unassignedOnly) list = list.filter(b => !b.assigned_to_id);
    return list.sort((a, b) => {
      // sort by trip start date, then by numeric bag_number where possible
      const tA = trips.find(t => t.trip_id === a.trip_id)?.start_date || '';
      const tB = trips.find(t => t.trip_id === b.trip_id)?.start_date || '';
      if (tA !== tB) return tA.localeCompare(tB);
      const nA = parseInt(a.bag_number, 10);
      const nB = parseInt(b.bag_number, 10);
      if (!isNaN(nA) && !isNaN(nB)) return nA - nB;
      return String(a.bag_number).localeCompare(String(b.bag_number));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bags, tripFilterSet, unassignedOnly, trips]);

  // ── Available options for filters ──────────────────────────
  const tripOptions = trips.map(t => ({ value: t.trip_id, label: t.name }));
  const stateOptions = useMemo(() => {
    const set = new Set();
    participants.forEach(p => {
      const s = (p.profile?.state || '').toUpperCase();
      if (s) set.add(s);
    });
    return Array.from(set).sort().map(s => ({ value: s, label: s }));
  }, [participants]);

  // ── ACTIONS ────────────────────────────────────────────────
  const handleCreateBag = async () => {
    if (!bagForm.bag_number) return;
    try {
      await api.post('/inventory/bags', {
        bag_number: bagForm.bag_number,
        trip_id: bagForm.trip_id || null,
        is_anesthesia: false,
      });
      toast.success(bagForm.trip_id ? 'Bag created' : 'Bag created (unassigned)');
      setShowCreateDialog(false);
      setBagForm({ bag_number: '', trip_id: '' });
      fetchAllBags();
    } catch { toast.error('Failed to create bag'); }
  };

  const handleUpdateStatus = async (bagId, status) => {
    try {
      await api.put(`/inventory/bags/${bagId}/status?status=${status}`);
      toast.success(`Bag → ${STATUS_CONFIG[status]?.label}`);
      fetchAllBags(); fetchInventoryItems();
    } catch { toast.error('Failed to update status'); }
  };

  const handleRevert = async (bag) => {
    if (bag.status === 'arrived') { setActiveBag(bag); setShowRevertConfirm(true); return; }
    await doRevert(bag.bag_id);
  };

  const doRevert = async (bagId) => {
    try {
      const res = await api.put(`/inventory/bags/${bagId}/revert`);
      toast.success(`Reverted to ${STATUS_CONFIG[res.data.new_status]?.label}`);
      setShowRevertConfirm(false); setActiveBag(null);
      fetchAllBags(); fetchInventoryItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot revert'); }
  };

  const handleDeleteBag = async (bagId) => {
    if (!window.confirm('Delete this bag? Any packed items will be returned to US Warehouse.')) return;
    try {
      await api.delete(`/inventory/bags/${bagId}`);
      toast.success('Bag deleted');
      fetchAllBags(); fetchInventoryItems();
    } catch { toast.error('Failed to delete bag'); }
  };

  // Direct (unconfirmed) assign — called after any controlled-substance confirmation
  const performAssign = async (bagId, userId, tripId) => {
    try {
      await api.post(`/inventory/bags/${bagId}/assign`, {
        bag_id: bagId, user_id: userId, trip_id: tripId,
      });
      toast.success('Bag assigned');
      fetchAllBags();
    } catch { toast.error('Failed to assign bag'); }
  };

  // Assign initiator — shows controlled warning if needed
  const requestAssign = (bag, participant) => {
    const userId = participant.user?.user_id || participant.user_id;
    const participantName = participantDisplayName(participant);
    if (bag.has_controlled) {
      setPendingAssignment({
        bagId: bag.bag_id, userId, tripId: bag.trip_id,
        bagNumber: bag.bag_number, participantName,
      });
    } else {
      performAssign(bag.bag_id, userId, bag.trip_id);
    }
  };

  const confirmPendingAssign = async () => {
    if (!pendingAssignment) return;
    await performAssign(pendingAssignment.bagId, pendingAssignment.userId, pendingAssignment.tripId);
    setPendingAssignment(null);
  };

  const handleUnassign = async (bagId) => {
    try {
      await api.delete(`/inventory/bags/${bagId}/assign`);
      toast.success('Bag unassigned');
      fetchAllBags();
    } catch { toast.error('Failed to unassign bag'); }
  };

  const handleSaveContents = async () => {
    const contents = packItems
      .filter(p => (parseInt(p.quantity, 10) || 0) > 0)
      .map(p => ({ item_id: p.item_id, quantity: parseInt(p.quantity, 10) || 0 }));
    try {
      await api.put(`/inventory/bags/${activeBag.bag_id}/contents`, { contents });
      toast.success('Bag contents updated');
      setShowPackDialog(false); setActiveBag(null); setPackItems([]);
      fetchAllBags(); fetchInventoryItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update bag contents'); }
  };

  const handleCreateNewItemInBag = async () => {
    if (!newItemForm?.name?.trim() || !newItemForm?.type_id || !(parseInt(newItemForm.quantity, 10) > 0)) {
      toast.error('Name, type, and a quantity greater than zero are required');
      return;
    }
    try {
      const qty = parseInt(newItemForm.quantity, 10);
      const res = await api.post(`/inventory/bags/${activeBag.bag_id}/add-new-item`, {
        name: newItemForm.name.trim(),
        type_id: newItemForm.type_id,
        quantity: qty,
        unit_of_measurement: newItemForm.unit_of_measurement?.trim() || null,
        unit_cost: newItemForm.unit_cost ? parseFloat(newItemForm.unit_cost) : null,
        controlled_substance: !!newItemForm.controlled_substance,
      });
      toast.success('New item added to bag and to inventory');

      const newItem = res.data?.item || {};
      const typeName = inventoryTypes.find(t => t.type_id === newItem.type_id)?.name || '';

      // Optimistically update local state so the item renders immediately
      // with its real name/type instead of "(removed item)" (closures over
      // inventoryItems don't see the just-refetched list until re-render).
      setInventoryItems(prev => [...prev, { ...newItem, type_name: typeName }]);
      setPackItems(prev => [
        {
          item_id: newItem.item_id,
          name: newItem.name,
          type_id: newItem.type_id || '',
          type_name: typeName,
          warehouse: 0,
          in_bag: qty,
          max: qty,
          quantity: qty,
          _in_bag: true,
        },
        ...prev,
      ]);

      setNewItemForm(null);
      // Background refresh so subsequent openings and other tabs stay in sync
      fetchInventoryItems();
      fetchAllBags();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add new item');
    }
  };

  const openPackDialog = (bag) => {
    setActiveBag(bag);
    // Build a merged list: everything currently in the bag (in-bag qty preserved as `quantity`),
    // plus all inventory items with any warehouse stock. Warehouse and in-bag counts drive the
    // per-row max ceiling ("can go up to warehouse + in-bag").
    const currentByItem = {};
    (bag.contents || []).forEach(c => { currentByItem[c.item_id] = c.quantity; });

    // Union of ids we care about: items currently in bag OR items available in inventory
    const rows = [];
    const seen = new Set();

    // 1) Items currently in the bag (may or may not still exist in inventory list)
    (bag.contents || []).forEach(c => {
      const inv = inventoryItems.find(i => i.item_id === c.item_id);
      rows.push({
        item_id: c.item_id,
        name: inv?.name || null,       // null → ghost
        type_id: inv?.type_id || '',
        type_name: inv?.type_name || '',
        warehouse: inv ? (inv.qty_us_warehouse || 0) : 0,
        in_bag: c.quantity,
        max: (inv ? (inv.qty_us_warehouse || 0) : 0) + c.quantity,
        quantity: c.quantity,          // starting target = current
        _in_bag: true,
      });
      seen.add(c.item_id);
    });
    // 2) Items available in inventory that aren't already in the bag
    inventoryItems.forEach(item => {
      if (seen.has(item.item_id)) return;
      if ((item.qty_us_warehouse || 0) <= 0) return;
      rows.push({
        item_id: item.item_id,
        name: item.name,
        type_id: item.type_id || '',
        type_name: item.type_name || '',
        warehouse: item.qty_us_warehouse || 0,
        in_bag: 0,
        max: item.qty_us_warehouse || 0,
        quantity: 0,
        _in_bag: false,
      });
    });
    setPackItems(rows);
    setPackSearch(''); setPackTypeFilter('all'); setShowPackDialog(true);
  };

  const toggleExpandContents = (bagId) => {
    setExpandedBags(prev => {
      const next = new Set(prev);
      if (next.has(bagId)) next.delete(bagId);
      else next.add(bagId);
      return next;
    });
  };

  // ── Bag row: participant picker (single-select, unassigned-first) ──
  const ParticipantPicker = ({ bag }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    // Scope to this bag's trip (a bag can only be assigned to a participant on the same trip),
    // then apply current state filter for consistency.
    const list = participants
      .filter(p => p._trip_id === bag.trip_id)
      .filter(p => !stateFilterSet || stateFilterSet.has((p.profile?.state || '').toUpperCase()))
      .filter(p => {
        if (!search) return true;
        const name = participantDisplayName(p);
        return name.toLowerCase().includes(search.toLowerCase());
      });
    // Sort: unassigned (0 bags this season) first, then assigned; within each, alphabetical
    const withMeta = list.map(p => {
      const uid = p.user?.user_id || p.user_id;
      const owned = bagsForUserOnTrip(uid, bag.trip_id);
      const name = participantDisplayName(p);
      return { p, uid, owned, name };
    });
    withMeta.sort((a, b) => {
      const ao = a.owned.length > 0 ? 1 : 0;
      const bo = b.owned.length > 0 ? 1 : 0;
      if (ao !== bo) return ao - bo;
      return a.name.localeCompare(b.name);
    });

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {bag.assigned_to ? (
            <button
              className="flex items-center gap-1.5 hover:bg-slate-100 rounded-lg px-1.5 py-0.5 transition-colors text-left min-w-0"
              data-testid={`assign-picker-${bag.bag_id}`}
            >
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <User className="w-3 h-3 text-primary" />
              </div>
              <span className="text-sm text-slate-700 truncate">{displayNameForUserId(bag.assigned_to_id, bag.assigned_to.name)}</span>
            </button>
          ) : (
            <button
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              data-testid={`assign-picker-${bag.bag_id}`}
            >Assign participant…</button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                autoFocus placeholder="Search participants…"
                value={search} onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid={`assign-picker-search-${bag.bag_id}`}
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {withMeta.length === 0 && (
              <div className="text-xs text-slate-400 px-3 py-3 text-center">No participants match filters</div>
            )}
            {withMeta.map(({ p, uid, owned, name }) => {
              const isCurrent = bag.assigned_to_id === uid;
              const hasBags = owned.length > 0 && !isCurrent;
              return (
                <div
                  key={uid}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setOpen(false);
                    if (isCurrent) return;
                    requestAssign(bag, p);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setOpen(false);
                      if (!isCurrent) requestAssign(bag, p);
                    }
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-primary/5 border-l-2 cursor-pointer select-none ${
                    isCurrent ? 'border-primary bg-primary/5' : hasBags ? 'border-slate-300 bg-slate-50' : 'border-transparent'
                  }`}
                  data-testid={`assign-picker-opt-${bag.bag_id}-${uid}`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${hasBags ? 'bg-slate-200' : 'bg-primary/10'}`}>
                    <User className={`w-3.5 h-3.5 ${hasBags ? 'text-slate-500' : 'text-primary'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`truncate ${hasBags ? 'text-slate-500' : 'text-slate-800 font-medium'}`}>{name}</div>
                    <div className="flex gap-1.5 items-center text-[10px] text-slate-400">
                      <span>{p._trip_name}</span>
                      {p.profile?.state && <span>· {p.profile.state.toUpperCase()}</span>}
                      {hasBags && <span className="text-slate-500 font-medium">· has {owned.length} bag{owned.length !== 1 ? 's' : ''}</span>}
                      {isCurrent && <span className="text-primary font-medium">· currently assigned</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {bag.assigned_to_id && (
            <div className="border-t p-2">
              <button
                onClick={() => { setOpen(false); handleUnassign(bag.bag_id); }}
                className="w-full text-xs text-red-600 hover:bg-red-50 rounded px-2 py-1.5 flex items-center justify-center gap-1.5"
                data-testid={`unassign-btn-${bag.bag_id}`}
              >
                <X className="w-3 h-3" /> Unassign
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  };

  // ── Participant row: bag multi-select picker ──
  const BagPicker = ({ participant }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const uid = participant.user?.user_id || participant.user_id;
    const owned = bagsForUserOnTrip(uid, participant._trip_id);
    // Bags user can pick from: unassigned bags in this participant's exact trip,
    // plus any bags already assigned to this participant in the same season (so they
    // appear as checked and can be unassigned from either week's row).
    const availableBags = bags.filter(b => {
      if (!b.assigned_to_id && b.trip_id === participant._trip_id) return true;
      if (b.assigned_to_id === uid && sameSeason(b.trip_id, participant._trip_id)) return true;
      return false;
    });
    const shown = availableBags.filter(b => !search || String(b.bag_number).toLowerCase().includes(search.toLowerCase()));
    shown.sort((a, b) => {
      const aOwn = a.assigned_to_id === uid ? 0 : 1;   // my bags first
      const bOwn = b.assigned_to_id === uid ? 0 : 1;
      if (aOwn !== bOwn) return aOwn - bOwn;
      const nA = parseInt(a.bag_number, 10); const nB = parseInt(b.bag_number, 10);
      if (!isNaN(nA) && !isNaN(nB)) return nA - nB;
      return String(a.bag_number).localeCompare(String(b.bag_number));
    });

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="flex flex-wrap gap-1 items-center hover:bg-slate-50 rounded px-1 py-0.5 min-h-[26px] w-full text-left"
            data-testid={`bag-picker-${uid}-${participant._trip_id}`}
          >
            {owned.length === 0 ? (
              participant.no_bag_needed ? (
                <span className="text-xs text-slate-400 italic">No bag needed</span>
              ) : (
                <span className="text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> No bag assigned
                </span>
              )
            ) : (
              owned.map(b => (
                <Badge key={b.bag_id} className={`text-xs font-medium ${b.has_controlled ? 'bg-red-100 text-red-700 border-red-200' : 'bg-primary/10 text-primary border-primary/20'}`}>
                  #{b.bag_number}{b.has_controlled ? ' ⚠' : ''}
                </Badge>
              ))
            )}
            <ChevronDown className="w-3 h-3 text-slate-400 ml-auto" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                autoFocus placeholder="Search bag #…"
                value={search} onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
            <div className="text-[10px] text-slate-400 mt-1">Bags in {participant._trip_name}</div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {shown.length === 0 && (
              <div className="text-xs text-slate-400 px-3 py-3 text-center">
                {availableBags.length === 0 ? 'No unassigned bags for this trip' : 'No bags match search'}
              </div>
            )}
            {shown.map(b => {
              const mine = b.assigned_to_id === uid;
              return (
                <div
                  key={b.bag_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setOpen(false);
                    if (mine) handleUnassign(b.bag_id);
                    else requestAssign(b, participant);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setOpen(false);
                      if (mine) handleUnassign(b.bag_id);
                      else requestAssign(b, participant);
                    }
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-primary/5 cursor-pointer select-none ${mine ? 'bg-primary/5' : ''}`}
                  data-testid={`bag-picker-opt-${uid}-${b.bag_id}`}
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${mine ? 'bg-primary border-primary text-white' : 'border-slate-300'}`}>
                    {mine && <Check className="w-3 h-3" />}
                  </span>
                  <span className="font-semibold text-slate-800">#{b.bag_number}</span>
                  {b.has_controlled && (
                    <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200"><Pill className="w-2.5 h-2.5 mr-0.5" />Controlled</Badge>
                  )}
                  <span className="ml-auto text-[10px] text-slate-400">{b.item_count} items</span>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  // ── Header actions (top bar) ──────────────────────────────
  const headerActions = (
    <>
      <Button onClick={() => setShowCreateDialog(true)} className="rounded-full" data-testid="create-bag-btn">
        <Plus className="w-4 h-4 mr-2" />New Bag
      </Button>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="rounded-full h-9" data-testid="print-manifest-btn">
            <Printer className="w-4 h-4 mr-1.5" />Print
            <ChevronDown className="w-3.5 h-3.5 opacity-60 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-1" align="end">
          {trips.length === 0 && <div className="text-xs text-slate-400 px-3 py-2">No trips</div>}
          {trips.map(t => (
            <a
              key={t.trip_id}
              href={`${process.env.REACT_APP_BACKEND_URL}/api/inventory/bags/print?trip_id=${t.trip_id}`}
              target="_blank" rel="noopener noreferrer"
              className="block px-3 py-2 rounded text-sm hover:bg-primary/5"
              data-testid={`print-manifest-trip-${t.trip_id}`}
            >{t.name}</a>
          ))}
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="rounded-full h-9" data-testid="export-manifest-btn">
            <Download className="w-4 h-4 mr-1.5" />Manifest
            <ChevronDown className="w-3.5 h-3.5 opacity-60 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-1" align="end">
          {trips.length === 0 && <div className="text-xs text-slate-400 px-3 py-2">No trips</div>}
          {trips.map(t => (
            <a
              key={t.trip_id}
              href={`${process.env.REACT_APP_BACKEND_URL}/api/inventory/bags/manifest-excel/${t.trip_id}`}
              className="block px-3 py-2 rounded text-sm hover:bg-primary/5"
              data-testid={`export-manifest-trip-${t.trip_id}`}
            >{t.name}</a>
          ))}
        </PopoverContent>
      </Popover>
    </>
  );

  // ── Bag row renderer ───────────────────────────────────────
  const renderBagRow = (bag) => {
    const isExpanded = expandedBags.has(bag.bag_id);
    // Inline preview of first 3 items with quantities
    const previewItems = (bag.contents || []).slice(0, 3).map(c => `${itemNameById(c.item_id)} (${c.quantity} ${itemUnitById(c.item_id)})`);
    const moreCount = (bag.contents || []).length - previewItems.length;
    return (
      <React.Fragment key={bag.bag_id}>
        <div className="grid grid-cols-[60px_100px_minmax(260px,1fr)_220px_auto] gap-3 items-start px-3 py-2.5 hover:bg-primary/5 border-b border-slate-100 transition-colors min-w-[960px]" data-testid={`bag-row-${bag.bag_id}`}>
          {/* Bag # */}
          <div className="pt-0.5">
            <span className="font-semibold text-slate-800">#{bag.bag_number}</span>
          </div>

          {/* Week */}
          <div className="pt-0.5">
            {bag.trip_id
              ? <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">{tripName(bag.trip_id)}</Badge>
              : <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">No trip</Badge>}
          </div>

          {/* Contents preview + status/badges */}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <StatusBadge status={bag.status} />
              {bag.has_controlled && (
                <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200">
                  <Pill className="w-2.5 h-2.5 mr-0.5" />Controlled
                </Badge>
              )}
            </div>
            {bag.item_count > 0 ? (
              <button
                onClick={() => toggleExpandContents(bag.bag_id)}
                className="text-xs text-slate-600 hover:text-primary text-left w-full"
                data-testid={`toggle-contents-${bag.bag_id}`}
                title="Click to expand full contents"
              >
                <span className="text-slate-500">{previewItems.join(', ')}</span>
                {moreCount > 0 && <span className="text-slate-400 ml-1">+{moreCount} more</span>}
              </button>
            ) : (
              <button
                onClick={() => toggleExpandContents(bag.bag_id)}
                className="text-xs text-slate-400 italic hover:text-primary text-left"
                data-testid={`toggle-contents-${bag.bag_id}`}
              >
                Empty
              </button>
            )}
          </div>

          {/* Assigned participant picker */}
          <div className="pt-0.5">
            <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Assigned</div>
            {bag.trip_id
              ? <ParticipantPicker bag={bag} />
              : <span className="text-[11px] text-slate-400 italic" title="Assign a trip to this bag before you can assign a participant">Assign trip first</span>}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 pt-0.5">
            {bag.status === 'packing' && (
              <>
                <Button variant="outline" size="sm" className="h-7 text-xs rounded-full"
                  onClick={() => openPackDialog(bag)} data-testid={`pack-btn-${bag.bag_id}`}>
                  <Package className="w-3 h-3 mr-1" />Add Items
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => handleUpdateStatus(bag.bag_id, 'packed')} data-testid={`mark-packed-btn-${bag.bag_id}`}>
                  <Check className="w-3 h-3 mr-1" />Mark Packed
                </Button>
              </>
            )}
            {bag.status === 'packed' && (
              <Button variant="outline" size="sm" className="h-7 text-xs rounded-full border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={() => handleUpdateStatus(bag.bag_id, 'in_transit')} data-testid={`mark-transit-btn-${bag.bag_id}`}>
                <Plane className="w-3 h-3 mr-1" />Transit
              </Button>
            )}
            {bag.status === 'in_transit' && (
              <Button size="sm" className="h-7 text-xs rounded-full bg-green-600 hover:bg-green-700"
                onClick={() => handleUpdateStatus(bag.bag_id, 'arrived')} data-testid={`confirm-arrived-btn-${bag.bag_id}`}>
                <Check className="w-3 h-3 mr-1" />Arrived
              </Button>
            )}
            {PREVIOUS_STATUS[bag.status] && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-orange-500 hover:bg-orange-50"
                onClick={() => handleRevert(bag)} title="Revert status"
                data-testid={`revert-btn-${bag.bag_id}`}>
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-300 hover:text-red-500 hover:bg-red-50"
              onClick={() => handleDeleteBag(bag.bag_id)} title="Delete"
              data-testid={`delete-bag-${bag.bag_id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {isExpanded && (
          <div className="bg-slate-50 border-b border-slate-100 px-4 py-3 min-w-[960px]" data-testid={`contents-panel-${bag.bag_id}`}>
            {(!bag.contents || bag.contents.length === 0) ? (
              <p className="text-xs text-slate-400">No items packed.</p>
            ) : (
              <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                {bag.contents.map(c => (
                  <li key={c.item_id} className="flex justify-between border-b border-slate-200/60 py-1">
                    <span className="text-slate-600 truncate">{itemNameById(c.item_id)}</span>
                    <span className="font-medium text-slate-800 ml-2">{c.quantity} {itemUnitById(c.item_id)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  // ── Participant row renderer (columnized) ─────────────────
  const renderParticipantRow = (p) => {
    const uid = p.user?.user_id || p.user_id;
    const name = participantDisplayName(p);
    const roles = p.profile?.work_roles || [];
    const state = (p.profile?.state || '').toUpperCase();
    return (
      <div
        key={`${uid}-${p._trip_id}`}
        className="grid grid-cols-[minmax(0,2fr)_60px_minmax(0,1.2fr)_minmax(0,1.2fr)_260px] gap-3 items-center px-3 py-2 hover:bg-primary/5 border-b border-slate-100"
        data-testid={`participant-row-${uid}-${p._trip_id}`}
      >
        <div className="min-w-0">
          <span className="font-medium text-sm text-slate-800 truncate block" title={name}>{name}</span>
        </div>
        <div>
          {state ? <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">{state}</Badge> : <span className="text-[10px] text-slate-300">—</span>}
        </div>
        <div className="min-w-0">
          <span className="text-xs text-slate-600 truncate block" title={p._trip_name}>{p._trip_name}</span>
        </div>
        <div className="min-w-0">
          {roles[0]
            ? <span className="text-xs text-primary font-medium truncate block" title={roles.join(', ')}>{roles[0]}</span>
            : <span className="text-[10px] text-slate-300">—</span>}
        </div>
        <div>
          <BagPicker participant={p} />
        </div>
      </div>
    );
  };

  // ── Summary counts ────────────────────────────────────────
  const totalBags = filteredBags.length;
  const unassignedBagsCount = filteredBags.filter(b => !b.assigned_to_id).length;
  const controlledCount = filteredBags.filter(b => b.has_controlled).length;
  const participantsNeedingBags = filteredParticipants.filter(p => !p.no_bag_needed && bagsForUserOnTrip(p.user?.user_id || p.user_id, p._trip_id).length === 0).length;

  return (
    <PageLayout title="Transport Bags" headerSub="Pack, assign, and track bags across trips" headerActions={headerActions}>

      {/* ── Filter bar ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex flex-wrap gap-2 items-center" data-testid="filter-bar">
        <Filter className="w-4 h-4 text-slate-400" />
        <MultiSelectFilter
          label="Week/Trip" icon={Plane} options={tripOptions}
          selected={selectedTripIds} onChange={setSelectedTripIds}
          testId="filter-trip"
        />
        <MultiSelectFilter
          label="State" options={stateOptions}
          selected={selectedStates} onChange={setSelectedStates}
          testId="filter-state"
        />
        <label className="flex items-center gap-2 text-sm text-slate-600 ml-1 cursor-pointer" data-testid="filter-unassigned-label">
          <Checkbox checked={unassignedOnly} onCheckedChange={setUnassignedOnly} data-testid="filter-unassigned" />
          Unassigned only
        </label>
      </div>

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-3" data-testid="bags-tabs">
        <button
          onClick={() => setActiveTab('bags')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
            activeTab === 'bags'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
          }`}
          data-testid="tab-bags"
        >
          <Briefcase className="w-4 h-4" />
          Bags
          <span className="text-xs font-normal text-slate-500">({totalBags})</span>
          {unassignedBagsCount > 0 && (
            <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200 ml-1">{unassignedBagsCount} unassigned</Badge>
          )}
          {controlledCount > 0 && (
            <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200">
              <Pill className="w-2.5 h-2.5 mr-0.5" />{controlledCount}
            </Badge>
          )}
        </button>
        <button
          onClick={() => setActiveTab('participants')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
            activeTab === 'participants'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
          }`}
          data-testid="tab-participants"
        >
          <Users className="w-4 h-4" />
          Participants
          <span className="text-xs font-normal text-slate-500">({filteredParticipants.length})</span>
          {participantsNeedingBags > 0 && (
            <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200 ml-1">{participantsNeedingBags} without bag</Badge>
          )}
        </button>
      </div>

      {/* ── Tab content ── */}
      {activeTab === 'bags' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="bags-panel">
          <div className="overflow-x-auto">
            {/* Column headers */}
            <div className="grid grid-cols-[60px_100px_minmax(260px,1fr)_220px_auto] gap-3 items-center px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-semibold text-slate-500 uppercase tracking-wide min-w-[960px]">
              <div>Bag #</div>
              <div>Week</div>
              <div>Contents</div>
              <div>Assigned To</div>
              <div className="text-right pr-2">Actions</div>
            </div>
            <div className="overflow-y-auto max-h-[calc(100vh-280px)] min-w-[960px]">
              {loading ? (
                <div className="text-center py-16 text-slate-400 text-sm">Loading bags…</div>
              ) : filteredBags.length === 0 ? (
                <div className="text-center py-16 text-slate-400 text-sm">
                  <Briefcase className="w-8 h-8 mx-auto text-slate-200 mb-2" />
                  {bags.length === 0 ? 'No bags created yet' : 'No bags match filters'}
                </div>
              ) : (
                filteredBags.map(renderBagRow)
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'participants' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="participants-panel">
          {/* Column headers */}
          <div className="grid grid-cols-[minmax(0,2fr)_60px_minmax(0,1.2fr)_minmax(0,1.2fr)_260px] gap-3 items-center px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
            <div>Name</div>
            <div>State</div>
            <div>Week</div>
            <div>Role</div>
            <div>Bags</div>
          </div>
          <div className="overflow-y-auto max-h-[calc(100vh-280px)]">
            {filteredParticipants.length === 0 ? (
              <div className="text-center py-16 text-slate-400 text-sm">
                <Users className="w-8 h-8 mx-auto text-slate-200 mb-2" />
                {participants.length === 0 ? 'No participants yet' : 'No participants match filters'}
              </div>
            ) : (
              filteredParticipants.map(renderParticipantRow)
            )}
          </div>
        </div>
      )}

      {/* ── Create Bag Dialog ── */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Transport Bag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Trip / Week</label>
              <Select value={bagForm.trip_id || '__none__'} onValueChange={v => setBagForm({ ...bagForm, trip_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="new-bag-trip-select"><SelectValue placeholder="Select trip…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No trip yet — unassigned</SelectItem>
                  {trips.map(t => <SelectItem key={t.trip_id} value={t.trip_id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!bagForm.trip_id && <p className="text-xs text-slate-400 mt-1">Bag will be created without a trip. You can assign one later.</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Bag Number</label>
              <Input
                value={bagForm.bag_number}
                onChange={e => setBagForm({ ...bagForm, bag_number: e.target.value })}
                placeholder="e.g., 1, 2, A1, A2"
                data-testid="bag-number-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateBag} disabled={!bagForm.bag_number} data-testid="save-bag-btn">
              Create Bag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Contents Dialog ── */}
      <Dialog open={showPackDialog} onOpenChange={setShowPackDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Edit Contents — Bag #{activeBag?.bag_number}</DialogTitle>
          </DialogHeader>
          {(() => {
            const matches = (item) => {
              if (packTypeFilter !== 'all') {
                const itemTypeId = item.type_id || '';
                if (packTypeFilter === '__none__') {
                  if (itemTypeId) return false;
                } else if (itemTypeId !== packTypeFilter) {
                  return false;
                }
              }
              if (!packSearch) return true;
              const q = packSearch.toLowerCase();
              return (item.name || '(removed item)').toLowerCase().includes(q)
                  || (item.type_name || '').toLowerCase().includes(q);
            };
            const inBagRows = packItems.filter(r => r._in_bag).filter(matches);
            const availableRows = packItems.filter(r => !r._in_bag && r.warehouse > 0).filter(matches);
            const totalTarget = packItems.reduce((s, p) => s + (parseInt(p.quantity, 10) || 0), 0);
            const setQty = (item_id, raw, max) => {
              const qty = Math.max(0, Math.min(parseInt(raw, 10) || 0, max));
              setPackItems(prev => prev.map(p => p.item_id === item_id ? { ...p, quantity: qty } : p));
            };
            return (
              <div className="flex-1 min-h-0 flex flex-col space-y-3 overflow-hidden">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      autoFocus
                      placeholder="Search all items by name or type…"
                      value={packSearch}
                      onChange={e => setPackSearch(e.target.value)}
                      className="pl-10"
                      data-testid="edit-contents-search"
                    />
                  </div>
                  <Select value={packTypeFilter} onValueChange={setPackTypeFilter}>
                    <SelectTrigger className="w-44" data-testid="edit-contents-type-filter">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {inventoryTypes.map(t => (
                        <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>
                      ))}
                      <SelectItem value="__none__">(No type)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full whitespace-nowrap"
                    onClick={() => setNewItemForm(newItemForm ? null : {
                      name: '', type_id: '', quantity: '', unit_of_measurement: '', unit_cost: '', controlled_substance: false
                    })}
                    data-testid="new-inventory-item-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />New Inventory Item
                  </Button>
                </div>

                {newItemForm && (
                  <div className="border border-primary/30 bg-primary/5 rounded-lg p-4 space-y-3 flex-shrink-0" data-testid="new-item-form">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-700">Create new item — will be added to inventory AND to this bag</p>
                      <button onClick={() => setNewItemForm(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Name *</label>
                        <Input value={newItemForm.name} onChange={e => setNewItemForm({ ...newItemForm, name: e.target.value })} placeholder="e.g., Surgical Mask, N95" data-testid="new-item-name" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Type *</label>
                        <Select value={newItemForm.type_id} onValueChange={v => setNewItemForm({ ...newItemForm, type_id: v })}>
                          <SelectTrigger data-testid="new-item-type"><SelectValue placeholder="Select type…" /></SelectTrigger>
                          <SelectContent>
                            {inventoryTypes.map(t => <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Quantity to add to bag *</label>
                        <Input type="number" min="1" value={newItemForm.quantity} onChange={e => setNewItemForm({ ...newItemForm, quantity: e.target.value })} placeholder="e.g., 50" data-testid="new-item-qty" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Unit</label>
                        <Input value={newItemForm.unit_of_measurement} onChange={e => setNewItemForm({ ...newItemForm, unit_of_measurement: e.target.value })} placeholder="each, box, pack" data-testid="new-item-unit" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Unit cost (optional)</label>
                        <Input type="number" step="0.01" min="0" value={newItemForm.unit_cost} onChange={e => setNewItemForm({ ...newItemForm, unit_cost: e.target.value })} placeholder="0.00" data-testid="new-item-cost" />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input type="checkbox" id="new-item-controlled" checked={newItemForm.controlled_substance} onChange={e => setNewItemForm({ ...newItemForm, controlled_substance: e.target.checked })} className="w-4 h-4" data-testid="new-item-controlled" />
                        <label htmlFor="new-item-controlled" className="text-xs text-slate-600">Controlled substance</label>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <Button variant="outline" size="sm" onClick={() => setNewItemForm(null)}>Cancel</Button>
                      <Button size="sm" onClick={handleCreateNewItemInBag} data-testid="new-item-save">Add to bag</Button>
                    </div>
                  </div>
                )}

                <div className="flex-1 min-h-0 overflow-y-auto border rounded-lg divide-y divide-slate-200">
                  {/* Currently in bag */}
                  {inBagRows.length > 0 && (
                    <div>
                      <div className="bg-slate-100 px-4 py-1.5 text-[11px] font-semibold text-slate-600 uppercase tracking-wide sticky top-0">
                        Currently in bag ({inBagRows.length})
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr className="text-[10px] uppercase text-slate-500">
                            <th className="px-4 py-1.5 text-left font-medium">Item</th>
                            <th className="px-4 py-1.5 text-left font-medium">Type</th>
                            <th className="px-4 py-1.5 text-right font-medium">In bag</th>
                            <th className="px-4 py-1.5 text-right font-medium">Warehouse</th>
                            <th className="px-4 py-1.5 text-right font-medium w-28">Target qty</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {inBagRows.map(item => {
                            const isGhost = !item.name;
                            return (
                              <tr key={item.item_id} className={isGhost ? 'bg-red-50/40' : ''} data-testid={`edit-content-row-${item.item_id}`}>
                                <td className={`px-4 py-2 font-medium ${isGhost ? 'text-red-500 italic' : 'text-slate-800'}`}>
                                  {item.name || '(removed item)'}
                                </td>
                                <td className="px-4 py-2 text-slate-500">{item.type_name || '—'}</td>
                                <td className="px-4 py-2 text-right text-slate-600">{item.in_bag}</td>
                                <td className="px-4 py-2 text-right text-blue-600">{item.warehouse}</td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number" min="0" max={item.max}
                                    value={item.quantity}
                                    onChange={e => setQty(item.item_id, e.target.value, item.max)}
                                    className="h-8 w-20 text-right ml-auto"
                                    disabled={isGhost}
                                    data-testid={`edit-content-qty-${item.item_id}`}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <button
                                    onClick={() => setQty(item.item_id, 0, item.max)}
                                    className="p-1 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded"
                                    title="Remove from bag"
                                    data-testid={`edit-content-remove-${item.item_id}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Add from inventory */}
                  <div>
                    <div className="bg-slate-100 px-4 py-1.5 text-[11px] font-semibold text-slate-600 uppercase tracking-wide sticky top-0">
                      Add from inventory ({availableRows.length})
                    </div>
                    {availableRows.length === 0 ? (
                      <div className="px-4 py-4 text-xs text-slate-400 text-center">
                        {(packSearch || packTypeFilter !== 'all') ? 'No inventory items match your filters.' : 'No available inventory items.'}
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr className="text-[10px] uppercase text-slate-500">
                            <th className="px-4 py-1.5 text-left font-medium">Item</th>
                            <th className="px-4 py-1.5 text-left font-medium">Type</th>
                            <th className="px-4 py-1.5 text-right font-medium">Warehouse</th>
                            <th className="px-4 py-1.5 text-right font-medium w-28">Add qty</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableRows.map(item => (
                            <tr key={item.item_id} data-testid={`edit-content-add-row-${item.item_id}`}>
                              <td className="px-4 py-2 font-medium text-slate-800">{item.name}</td>
                              <td className="px-4 py-2 text-slate-500">{item.type_name || '—'}</td>
                              <td className="px-4 py-2 text-right text-blue-600">{item.warehouse}</td>
                              <td className="px-4 py-2">
                                <Input
                                  type="number" min="0" max={item.max}
                                  value={item.quantity}
                                  onChange={e => setQty(item.item_id, e.target.value, item.max)}
                                  className="h-8 w-20 text-right ml-auto"
                                  data-testid={`edit-content-add-qty-${item.item_id}`}
                                />
                              </td>
                              <td className="w-10"></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                <p className="text-sm text-slate-500">Total after save: {totalTarget} units</p>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveContents} data-testid="confirm-pack-btn">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Controlled-substance warning ── */}
      <Dialog open={!!pendingAssignment} onOpenChange={(open) => !open && setPendingAssignment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pill className="w-5 h-5 text-red-500" />
              Controlled substance in this bag
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm text-slate-600 space-y-2">
            <p>
              Bag <strong>#{pendingAssignment?.bagNumber}</strong> contains at least one controlled substance and normally travels with a medical professional (anesthesiologist / CRNA).
            </p>
            <p>
              Confirm you want to assign it to <strong>{pendingAssignment?.participantName}</strong>?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAssignment(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={confirmPendingAssign} data-testid="confirm-controlled-assign-btn">
              Assign anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Revert Confirmation (arrived → in_transit) ── */}
      <Dialog open={showRevertConfirm} onOpenChange={setShowRevertConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Revert Bag #{activeBag?.bag_number}?
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm text-slate-600 space-y-2">
            <p>This bag is marked as <strong>Arrived</strong>. Its contents have already been transferred to Guatemala inventory.</p>
            <p>Reverting to <strong>In Transit</strong> will move those quantities back out of Guatemala and into In Bags.</p>
            <p className="text-orange-600 font-medium">Are you sure you want to reverse this transfer?</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRevertConfirm(false); setActiveBag(null); }}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => doRevert(activeBag.bag_id)} data-testid="confirm-revert-btn">
              <RotateCcw className="w-4 h-4 mr-2" />Yes, Revert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default TransportBags;
